import { useState, useEffect } from "react";
import { useTheme } from "../theme";
import { api } from "../api";

export default function Accounts({ token, tier, limits }) {
  const { accent, accentBg, accentBorder } = useTheme();
  const styles = getStyles(accent, accentBg, accentBorder);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [adding, setAdding]     = useState(false);
  const [cookie, setCookie]     = useState("");
  const [secret, setSecret]     = useState("");
  const [proxy,  setProxy]      = useState("");
  const [msg, setMsg]           = useState("");
  const [error, setError]       = useState("");
  const [editing2fa, setEditing2fa] = useState(null); // index of account being edited
  const [new2fa, setNew2fa]     = useState("");
  const [showEncTip, setShowEncTip] = useState(false);
  const [savedProxies, setSavedProxies] = useState([]);
  const [newProxyInput, setNewProxyInput] = useState("");
  const [proxyError, setProxyError] = useState("");
  const [selectedProxyId, setSelectedProxyId] = useState("");

  const fetchProxies = async () => {
    try {
      const res = await api.listProxies(token);
      setSavedProxies(res.proxies || []);
    } catch {}
  };

  useEffect(() => { fetchProxies(); }, []);

  const addProxyToPool = async () => {
    setProxyError("");
    try {
      const res = await api.addProxy(token, newProxyInput.trim());
      setSavedProxies(res.proxies || []);
      setNewProxyInput("");
    } catch (e) {
      setProxyError(e.message);
    }
  };

  const removeProxyFromPool = async (id) => {
    if (!window.confirm("Remove this proxy from your pool?")) return;
    const res = await api.removeProxy(token, id);
    setSavedProxies(res.proxies || []);
  };

  const maskProxy = (p) => {
    // Show ip:port only, hide credentials
    return p.includes("@") ? p.split("@")[1] : p;
  };

  const fetchAccounts = async () => {
    const cfg = await api.getConfig(token);
    setAccounts(cfg.accounts || []);
    setLoading(false);
  };

  useEffect(() => { fetchAccounts(); }, []);

  const addAccount = async () => {
    if (!cookie.trim()) { setError("Cookie required"); return; }
    const resolvedProxy = selectedProxyId
      ? (savedProxies.find(p => p.id === selectedProxyId)?.proxy || "")
      : proxy.trim();
    if (!resolvedProxy) { setError("A proxy is required for every account. Add one below or pick from your pool"); return; }
    setMsg(""); setError("");
    try {
      const res = await api.addAccount(token, { cookie: cookie.trim(), secret: secret.trim(), proxy: resolvedProxy });
      setMsg(`✓ Logged in as ${res.username} (${res.account_count} total)`);
      setCookie(""); setSecret(""); setProxy(""); setSelectedProxyId(""); setAdding(false);
      fetchAccounts();
    } catch (e) {
      setError(e.message);
    }
  };

  const removeAccount = async (i) => {
    if (!window.confirm(`Remove account ${accounts[i].Name || i+1}?`)) return;
    await api.removeAccount(token, i);
    setAccounts(prev => prev.filter((_, idx) => idx !== i));
  };

  const save2fa = async (i) => {
    await api.update2fa(token, i, new2fa);
    setAccounts(prev => {
      const updated = [...prev];
      updated[i] = { ...updated[i], Secret: new2fa ? "***" : "" };
      return updated;
    });
    setEditing2fa(null);
    setNew2fa("");
  };

  const maxAccounts = limits?.maxAccounts ?? 2;
  const atCap = accounts.length >= maxAccounts;
  const displayName = limits?.displayName || tier;

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <div>
          <h2 style={{ color:"#fff", margin:0, fontSize:18 }}>Accounts</h2>
          <div style={{ color:"#555", fontSize:12, marginTop:4 }}>{accounts.length} / {maxAccounts} account(s) used ({displayName} plan)</div>
        </div>
        {!atCap ? (
          <button style={styles.btn} onClick={() => setAdding(!adding)}>
            {adding ? "Cancel" : "+ Add Account"}
          </button>
        ) : (
          <button style={styles.btnLockedUpgrade} onClick={() => alert("Upgrade your plan to add more accounts!")}>
            🔒 Upgrade to add more
          </button>
        )}
      </div>

      <div
        style={styles.encBadge}
        onMouseEnter={() => setShowEncTip(true)}
        onMouseLeave={() => setShowEncTip(false)}
      >
        <span style={styles.encDot}>🔒</span>
        <span>Your cookies and 2FA secrets are encrypted</span>
        {showEncTip && (
          <div style={styles.encTooltip}>
            Your <code style={styles.encCode}>.ROBLOSECURITY</code> cookie and 2FA secret are encrypted
            before being stored. Even in the unlikely event our database was ever compromised, this
            data couldn't be read without also separately compromising our server's encryption key,
            which is stored completely separately. See our Privacy Policy for details.
          </div>
        )}
      </div>

      <div style={styles.proxyPoolBox}>
        <div style={styles.proxyPoolHeader}>
          <div>
            <div style={styles.proxyPoolTitle}>Proxy Pool</div>
            <div style={styles.proxyPoolSub}>
              Add proxies here once, then pick them from a dropdown when adding accounts. Checked every 24h.
              If one goes bad you'll get pinged, and it's auto-removed after 24h of continuous failures
              (rechecked every 6h in the meantime). A proxy is now required for every account.
              Get a <b>Dedicated IPv4 HTTP/SOCKS5</b> proxy at{" "}
              <a href="https://proxyline.net/" target="_blank" rel="noopener noreferrer" style={{color:accent}}>proxyline.net</a> if you need one.
            </div>
          </div>
        </div>
        <div style={styles.proxyAddRow}>
          <input
            style={styles.input}
            placeholder="user:pass@ip:port, e.g. exampleUser:examplePass@203.0.113.10:8080"
            value={newProxyInput}
            onChange={e => setNewProxyInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addProxyToPool()}
          />
          <button style={styles.btn} onClick={addProxyToPool}>+ Add</button>
        </div>
        {proxyError && <div style={styles.error}>{proxyError}</div>}
        {savedProxies.length > 0 && (
          <div style={styles.proxyList}>
            {savedProxies.map(p => (
              <div key={p.id} style={styles.proxyRow}>
                <span style={{
                  ...styles.proxyStatusDot,
                  background: p.status === "good" ? accent : p.status === "bad" ? "#ef4444" : "#555",
                }} />
                <span style={styles.proxyAddr}>{maskProxy(p.proxy)}</span>
                <span style={styles.proxyStatusText}>
                  {p.status === "good" ? "Working" : p.status === "bad" ? "Failing" : "Not checked yet"}
                </span>
                <button style={styles.proxyRemoveBtn} onClick={() => removeProxyFromPool(p.id)}>Remove</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {adding && (
        <div style={styles.addForm}>
          <div style={styles.formTitle}>ADD ROBLOX ACCOUNT</div>
          <div style={styles.field}>
            <label style={styles.label}>.ROBLOSECURITY Cookie</label>
            <textarea style={styles.textarea} placeholder="Paste your .ROBLOSECURITY cookie..." value={cookie} onChange={e=>setCookie(e.target.value)} rows={3} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>2FA Secret (optional, can add later)</label>
            <input style={styles.input} placeholder="Base32 2FA secret" value={secret} onChange={e=>setSecret(e.target.value)} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Proxy <span style={{color:"#ef4444"}}>*required</span></label>
            <div style={styles.proxyRecommend}>
              Don't have one? Get a <b>Dedicated IPv4 HTTP/SOCKS5</b> proxy at{" "}
              <a href="https://proxyline.net/" target="_blank" rel="noopener noreferrer" style={{color:accent}}>proxyline.net</a>
            </div>
            {savedProxies.length > 0 && (
              <select
                style={{...styles.input, marginBottom: 8}}
                value={selectedProxyId}
                onChange={e => { setSelectedProxyId(e.target.value); if (e.target.value) setProxy(""); }}
              >
                <option value="">Type a new one below instead</option>
                {savedProxies.map(p => (
                  <option key={p.id} value={p.id}>
                    {maskProxy(p.proxy)} {p.status === "good" ? "✓" : p.status === "bad" ? "⚠ bad" : ""}
                  </option>
                ))}
              </select>
            )}
            <input
              style={styles.input}
              placeholder="e.g. exampleUser:examplePass@203.0.113.10:8080"
              value={proxy}
              onChange={e => { setProxy(e.target.value); if (e.target.value) setSelectedProxyId(""); }}
            />
          </div>
          {error && <div style={styles.error}>{error}</div>}
          {msg   && <div style={styles.success}>{msg}</div>}
          <button style={styles.btn} onClick={addAccount}>Add & Verify Account</button>
        </div>
      )}

      {loading ? (
        <div style={{color:"#555",padding:20}}>Loading...</div>
      ) : accounts.length === 0 ? (
        <div style={styles.empty}>No accounts added yet.</div>
      ) : (
        <div style={styles.list}>
          {accounts.map((acc, i) => (
            <div key={i} style={styles.accCard}>
              {acc.AvatarUrl
                ? <img src={acc.AvatarUrl} alt="" style={styles.accAvatar} />
                : <div style={styles.accIcon}>{(acc.Name||"?")[0].toUpperCase()}</div>
              }
              <div style={{flex:1}}>
                <div style={{color:"#fff",fontSize:14,fontWeight:600}}>{acc.Name||`Account ${i+1}`}</div>
                <div style={{color:"#555",fontSize:12,marginTop:2,display:"flex",gap:10,flexWrap:"wrap"}}>
                  <span>2FA: {acc.Secret ? <span style={{color:accent}}>✓ Set</span> : <span style={{color:"#f59e0b"}}>Not set</span>}</span>
                  <span>Proxy: {acc.Proxy ? <span style={{color:accent}}>✓ Set</span> : <span style={{color:"#555"}}>None</span>}</span>
                  {acc.Disabled && <span style={{color:"#ef4444"}}>DISABLED</span>}
                  {acc.Invalid  && <span style={{color:"#ef4444"}}>INVALID</span>}
                </div>

                {/* 2FA edit inline */}
                {editing2fa === i && (
                  <div style={{marginTop:8,display:"flex",gap:8}}>
                    <input
                      style={{...styles.input,width:220,padding:"4px 8px",fontSize:12}}
                      placeholder="Base32 2FA secret"
                      value={new2fa}
                      onChange={e=>setNew2fa(e.target.value)}
                    />
                    <button style={{...styles.btn,padding:"4px 12px",fontSize:12}} onClick={()=>save2fa(i)}>Save</button>
                    <button style={{...styles.btnGhost,padding:"4px 10px",fontSize:12}} onClick={()=>setEditing2fa(null)}>Cancel</button>
                  </div>
                )}
              </div>

              <div style={{display:"flex",gap:8,flexShrink:0}}>
                <button style={styles.btnSmall} onClick={()=>{setEditing2fa(i);setNew2fa(acc.Secret||"");}}>
                  {acc.Secret ? "Edit 2FA" : "+ 2FA"}
                </button>
                <button style={{...styles.btnSmall,background:"#7f1d1d",color:"#fca5a5"}} onClick={()=>removeAccount(i)}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {atCap && (
        <div style={styles.upgradeNote}>
          🔒 {displayName} plan limit reached ({maxAccounts} accounts max). Upgrade for more.
        </div>
      )}
    </div>
  );
}

const getStyles = (accent, accentBg, accentBorder) => ({
  encBadge:    { position:"relative", display:"inline-flex", alignItems:"center", gap:8, background:"#0f1f14", border:"1px solid #14532d", borderRadius:8, padding:"8px 14px", color:accent, fontSize:12.5, fontWeight:600, marginBottom:20, cursor:"default", width:"fit-content" },
  encDot:      { fontSize:13 },
  encTooltip:  { position:"absolute", top:"calc(100% + 8px)", left:0, width:340, background:"#111", border:"1px solid #1a1a1a", borderRadius:10, padding:"14px 16px", color:"#aaa", fontSize:12.5, fontWeight:400, lineHeight:1.6, boxShadow:"0 12px 32px rgba(0,0,0,0.5)", zIndex:30 },
  encCode:     { background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:4, padding:"1px 5px", fontFamily:"monospace", fontSize:11.5, color:accent },
  encLink:     { color:accent },
  proxyPoolBox:  { background:"#0f0f0f", border:"1px solid #1a1a1a", borderRadius:10, padding:18, marginBottom:20 },
  proxyPoolHeader: { marginBottom:14 },
  proxyPoolTitle:{ color:"#fff", fontSize:14, fontWeight:700, marginBottom:4 },
  proxyPoolSub:  { color:"#666", fontSize:11.5, lineHeight:1.5, maxWidth:640 },
  proxyAddRow:   { display:"flex", gap:8 },
  proxyList:     { display:"flex", flexDirection:"column", gap:6, marginTop:14 },
  proxyRow:      { display:"flex", alignItems:"center", gap:10, background:"#141414", border:"1px solid #1f1f1f", borderRadius:8, padding:"8px 12px" },
  proxyStatusDot:{ width:8, height:8, borderRadius:"50%", flexShrink:0 },
  proxyAddr:     { color:"#ccc", fontSize:12.5, fontFamily:"monospace", flex:1 },
  proxyStatusText:{ color:"#666", fontSize:11.5 },
  proxyRemoveBtn:{ padding:"4px 10px", background:"#7f1d1d", border:"none", borderRadius:6, color:"#fff", fontWeight:600, cursor:"pointer", fontSize:11 },
  proxyRecommend:{ background:"#0f1f14", border:"1px solid #14532d", borderRadius:6, padding:"6px 10px", color:"#aaa", fontSize:11.5, marginBottom:8, lineHeight:1.5 },
  btn:         { padding:"10px 18px", background:accent, border:"none", borderRadius:8, color:"#000", fontWeight:700, cursor:"pointer", fontSize:13 },
  btnLockedUpgrade: { padding:"10px 18px", background:"#1a1a0a", border:"1px solid #a16207", borderRadius:8, color:"#a16207", fontWeight:700, cursor:"pointer", fontSize:13 },
  btnGhost:    { padding:"10px 18px", background:"#1a1a1a", border:"1px solid #333", borderRadius:8, color:"#888", cursor:"pointer", fontSize:13 },
  btnSmall:    { padding:"5px 12px", background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:6, color:"#888", cursor:"pointer", fontSize:12 },
  addForm:     { background:"#0f0f0f", border:"1px solid #1a1a1a", borderRadius:8, padding:24, marginBottom:20 },
  formTitle:   { color:"#555", fontSize:11, letterSpacing:1, marginBottom:16 },
  field:       { marginBottom:16 },
  label:       { display:"block", color:"#666", fontSize:12, marginBottom:6 },
  input:       { width:"100%", background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:6, padding:"10px 12px", color:"#fff", fontSize:13, outline:"none", boxSizing:"border-box", fontFamily:"monospace" },
  textarea:    { width:"100%", background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:6, padding:"10px 12px", color:"#fff", fontSize:12, outline:"none", boxSizing:"border-box", fontFamily:"monospace", resize:"vertical" },
  error:       { color:"#ff4444", fontSize:13, marginBottom:12 },
  success:     { color:accent, fontSize:13, marginBottom:12 },
  empty:       { color:"#444", padding:"40px 20px", textAlign:"center", fontSize:14 },
  list:        { display:"flex", flexDirection:"column", gap:10 },
  accCard:     { background:"#0f0f0f", border:"1px solid #1a1a1a", borderRadius:8, padding:"14px 18px", display:"flex", alignItems:"flex-start", gap:14 },
  accAvatar:   { width:38, height:38, borderRadius:"50%", objectFit:"cover", flexShrink:0 },
  accIcon:     { width:38, height:38, borderRadius:"50%", background:"rgba(var(--rbx-accent-rgb,56,189,248),0.13)", color:"var(--rbx-accent,#38bdf8)", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:16, flexShrink:0 },
  upgradeNote: { marginTop:16, background:"#1a1a0a", border:"1px solid #333", borderRadius:8, padding:"12px 16px", color:"#a16207", fontSize:13 },
});